// ========================================================================
// api.js - COMPLETE AND CORRECTED VERSION
// ========================================================================

// For development, use a relative path. This triggers the Webpack proxy.
// For production, you will use your real, secure (HTTPS) API URL.
const API_BASE = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
  ? '/api/v1' // This will be proxied by webpack to your backend
  : 'https://backend.katip.site/api/v1'; // Backend API URL

/**
 * Handles user login.
 * @param {string} email
 * @param {string} password
 * @returns {Promise<object>}
 */
export async function login(email, password) {
  try {
    const response = await fetch(`${API_BASE}/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    });
    if (!response.ok) throw new Error(`Login failed: ${response.statusText}`);
    return response.json();
  } catch (error) {
    console.error("❌ API Error during login:", error);
    return { success: false, error: "Giriş yapılamadı." };
  }
}

// --- NEWLY ADDED FUNCTION ---
/**
 * Handles new user registration.
 * @param {string} email
 * @param {string} password
 * @returns {Promise<object>}
 */
export async function register(email, password) {
  try {
    const response = await fetch(`${API_BASE}/auth/register`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    });
    if (!response.ok) throw new Error(`Registration failed: ${response.statusText}`);
    return response.json();
  } catch (error) {
    console.error("❌ API Error during registration:", error);
    return { success: false, error: "Kayıt olunamadı." };
  }
}

/**
 * Handles sending a message to the AI backend.
 * @param {string} message - The user's prompt.
 * @param {object} context - The document context from Office.js.
 * @param {string} token - The user's authentication token.
 * @returns {Promise<object>}
 */
export async function edit(message, context, token) {
  const url = `${API_BASE}/messages`;
  console.log(`🚀 Making proxied request to: ${url}`);
  try {
    const headers = { 'Content-Type': 'application/json' };
    if (token) headers['Authorization'] = `Bearer ${token}`;

    const response = await fetch(url, {
      method: 'POST',
      headers: headers,
      body: JSON.stringify({ message, context }),
    });
    if (!response.ok) throw new Error(`API request failed: ${response.statusText}`);
    return response.json();
  } catch (error) {
    console.error("❌ API Error during edit:", error);
    return getMockEditResponse(message, context); // Fallback to mock
  }
}

/**
 * Sends a message, context and the full Word document as multipart/form-data.
 * Field name for the file is `wordDocument`.
 * @param {string} message
 * @param {object} context
 * @param {string} token
 * @param {Blob} wordDocument
 * @returns {Promise<object>}
 */
export async function editWithFormData(message, context, token, wordDocument) {
  const url = `${API_BASE}/messages`;
  try {
    const formData = new FormData();
    formData.append('userText', message);
    formData.append('selection', JSON.stringify(context, null, 4));
    formData.append('wordDocument', JSON.stringify(wordDocument, null, 4));
    
    const headers = {};
    if (token) headers['Authorization'] = `Bearer ${token}`; // Do NOT set Content-Type for FormData

    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: formData,
    });
    if (!response.ok) throw new Error(`API request failed: ${response.statusText}`);
    return response.json();
  } catch (error) {
    console.error('❌ API Error during editWithFormData:', error);
    return getMockEditResponse(message, context);
  }
}

// --- NEWLY ADDED FUNCTION ---
/**
 * Fetches the user's chat history.
 * @param {number} limit
 * @param {number} offset
 * @param {string} token
 * @returns {Promise<object>}
 */
export async function getHistory(limit = 10, offset = 0, token) {
  try {
    const response = await fetch(`${API_BASE}/history?limit=${limit}&offset=${offset}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!response.ok) throw new Error(`History fetch failed: ${response.statusText}`);
    return response.json();
  } catch (error) {
    console.error("❌ API Error during history fetch:", error);
    return { success: true, data: [] }; // Return empty history for mock
  }
}

// --- NEWLY ADDED FUNCTION ---
/**
 * Fetches available templates.
 * @param {string} token
 * @returns {Promise<object>}
 */
export async function getTemplates(token) {
  try {
    const response = await fetch(`${API_BASE}/templates`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!response.ok) throw new Error(`Templates fetch failed: ${response.statusText}`);
    return response.json();
  } catch (error) {
    console.error("❌ API Error during templates fetch:", error);
    return { success: true, data: [] }; // Return empty templates for mock
  }
}

/**
 * Generates a mock AI response for offline development.
 * @param {string} message
 * @param {object} context
 * @returns {Promise<object>}
 */
function getMockEditResponse(message, context) {
  const mockData = {
    action: 'REPLACE',
    content: `(Mock Response) You asked to process: "${context.selectedText}". Your prompt was: "${message}".`,
  };
  return new Promise(resolve => {
    setTimeout(() => {
      resolve(mockData);
    }, 500);
  });
}